import { SalesDocumentItem } from '../../types/sales-document-item';
import { SalesDocumentItemUpdateResponse } from '../../types/sales-document-item-requests';

export type DraftSalesDocumentItem = SalesDocumentItem & { _clientOnly?: boolean };


export type KeyedSalesDocumentItem = SalesDocumentItem & { _rowKey?: string };

/** Normalized product type — the backend's casing varies ('text' vs 'TEXT'),
    and a case miss silently renders a Textartikel like a regular article. */
export function productTypeOf(
  data: SalesDocumentItem | null | undefined
): string | null {
  return data?.product?.type?.toLowerCase() ?? null;
}

export function isSection(data: SalesDocumentItem | null | undefined): boolean {
  return productTypeOf(data) === 'section';
}

export function isText(data: SalesDocumentItem | null | undefined): boolean {
  return productTypeOf(data) === 'text';
}

export function isCustomizable(data: SalesDocumentItem | null | undefined): boolean {
  return productTypeOf(data) === 'customizable';
}

export function isReturnable(data: SalesDocumentItem | null | undefined): boolean {
  return productTypeOf(data) === 'returnable';
}

export function isFlatrate(data: SalesDocumentItem | null | undefined): boolean {
  return productTypeOf(data) === 'flat-rate';
}


export function isDraftRow(data: SalesDocumentItem | null | undefined): boolean {
  return !!(data as DraftSalesDocumentItem | null | undefined)?._clientOnly;
}

export function hasAvailableAction(
  data: SalesDocumentItem | null | undefined,
  key: string
): boolean {
  return !!data?.actions?.some((a) => a.key === key && a.reason === null);
}


export function isPreviewDraft(data: SalesDocumentItem | null | undefined): boolean {
  return isDraftRow(data) && !!data?.product?.key?.trim();
}


export function draftColumn(
  data: SalesDocumentItem | null | undefined
): 'product' | 'quantity' | null {
  if (!isDraftRow(data)) return null;
  return isPreviewDraft(data) ? 'quantity' : 'product';
}

/** Whether the row has a real article behind it, i.e. whether the Artikel side
    panel has anything to show. Abschnitt, Pauschale and Textartikel carry no
    product master data — the panel would only render empty sections. */
export function hasProductDetails(
  data: SalesDocumentItem | null | undefined
): boolean {
  if (!data) return false;
  return !isSection(data) && !isText(data) && !isFlatrate(data);
}

export function hidesAllValueColumns(
  data: SalesDocumentItem | null | undefined
): boolean {
  return isText(data);
}

export function hidesQuantityColumns(
  data: SalesDocumentItem | null | undefined
): boolean {
  return isSection(data) || isText(data);
}


export function isFullScopeChange(
  response: SalesDocumentItemUpdateResponse | null | undefined
): boolean {
  return response?.change?.scope === 'all';
}
